/** @jsxImportSource @emotion/react */
import { Form, FormikErrors, FormikTouched, FormikValues } from 'formik'
import styled from '@emotion/styled'
import { Button } from 'react-bootstrap'

import DateTimePicker from 'components/atoms/DateTimePicker'
import { followUpNotes } from 'components/organisms/note/followUpNotes'

export const TimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;

  .react-datepicker-wrapper {
    flex: 1;

    input {
      width: 100%;
      padding: 7px 8px;
    }
    svg {
      top: 9px !important;
      right: 7px !important;
    }
    button {
      margin-right: 20px;
    }
  }
`

interface IProps {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  values: FormikValues
  errors: FormikErrors<FormikValues>
  touched: FormikTouched<FormikValues>
  /** When true, human follow-up is locked on (e.g. the patient opted out of voice calls). */
  forceHumanFollowUp?: boolean
}

export const FollowUpForm = ({ setFieldValue, values, errors, touched, forceHumanFollowUp }: IProps) => {
  const humanFollowUpRequired = forceHumanFollowUp || Boolean(values.humanFollowUpRequired)

  return (
    <Form className="col-sm-12 p-3">
      <TimeWrapper>
        <div css={{ fontWeight: '500' }} className="mb-1">
          Follow-up Date
        </div>
        <DateTimePicker
          value={values.followUpDate}
          handleChange={(date) => setFieldValue('followUpDate', date)}
          minDate={new Date()}
          showTimeSelect={false}
          isClearable
          dateFormat="MMMM d, yyyy"
        />
      </TimeWrapper>
      <div className="row mt-2">
        <div className="col">
          <select
            className="form-control"
            name="followUpTemplate"
            defaultValue=""
            onChange={(e) => {
              setFieldValue('notes', e.target.value)
              e.currentTarget.value = ''
            }}>
            <option value="">Select Note Template</option>
            {followUpNotes.map((note, index) => (
              <option key={`${note.title}-${index}`} value={note.content}>
                {note.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-2">
        <textarea
          name="notes"
          className="form-control"
          placeholder="Notes"
          value={values.notes}
          onChange={(e) => setFieldValue('notes', e.target.value)}
          css={{
            borderColor: errors.notes && touched.notes ? 'red' : undefined,
          }}
        />
        {errors.notes && touched.notes && (
          <label
            css={{
              color: 'red',
              position: 'absolute',
              bottom: '-25px',
              left: '8px',
              fontWeight: 'normal !important',
            }}>
            {errors.notes}
          </label>
        )}
      </div>

      <div
        className="mt-3 p-3"
        css={{
          background: humanFollowUpRequired ? '#fff3cd' : '#ffffff',
          border: `1px solid ${humanFollowUpRequired ? '#ffc107' : '#dee2e6'}`,
          borderRadius: 6,
        }}>
        <label
          className="d-flex align-items-center mb-2"
          css={{ gap: 8, fontWeight: 600, cursor: forceHumanFollowUp ? 'not-allowed' : 'pointer' }}>
          <input
            type="checkbox"
            checked={humanFollowUpRequired}
            disabled={forceHumanFollowUp}
            onChange={(e) => {
              setFieldValue('humanFollowUpRequired', e.target.checked)
              if (!e.target.checked) {
                setFieldValue('humanFollowUpReason', '')
              }
            }}
          />
          Human follow-up required
        </label>
        <textarea
          name="humanFollowUpReason"
          className="form-control"
          disabled={!humanFollowUpRequired}
          placeholder="Reason for human follow-up"
          value={values.humanFollowUpReason ?? ''}
          onChange={(e) => setFieldValue('humanFollowUpReason', e.target.value)}
          css={{
            minHeight: 70,
            borderColor:
              errors.humanFollowUpReason && touched.humanFollowUpReason ? 'red' : undefined,
          }}
        />
        {errors.humanFollowUpReason && touched.humanFollowUpReason && (
          <div css={{ color: 'red', fontSize: 12, marginTop: 4 }}>
            {errors.humanFollowUpReason}
          </div>
        )}
        <small css={{ display: 'block', marginTop: 6, color: '#6c757d' }}>
          {forceHumanFollowUp
            ? 'Locked on: this patient has opted out of automated voice calls, so follow-ups must be handled by staff.'
            : 'Use this when the next follow-up should be handled by staff instead of automated outreach.'}
        </small>
      </div>

      <Button variant="outline-primary mt-2" type="submit">
        Update
      </Button>
    </Form>
  )
}
